import * as React from "react"

import { useMotionPreference } from "../utils/motion-preference"

const DOT_SPACING = 10
const DOT_RADIUS = 0.55
const REPEL_RADIUS = 145
const REPEL_STRENGTH = 42
const IDLE_HOLD_MS = 140
const RETURN_DURATION_MS = 1050
const POINTER_FOLLOW_EASE = 0.58

const InteractiveDotBackground = () => {
  const canvasRef = React.useRef(null)
  const { reduceMotion } = useMotionPreference()

  React.useEffect(() => {
    if (reduceMotion || typeof window === "undefined") return undefined

    const canvas = canvasRef.current
    const context = canvas?.getContext("2d", { alpha: true })
    if (!canvas || !context) return undefined

    let frameId = null
    let width = 0
    let height = 0
    let dpr = 1
    const bufferCanvas = document.createElement("canvas")
    const bufferContext = bufferCanvas.getContext("2d", { alpha: true })
    let targetPointer = null
    let pointer = null
    let lastPointerTime = 0

    if (!bufferContext) return undefined

    document.documentElement.classList.add("has-interactive-dot-background")

    const drawDot = (targetContext, x, y, alpha, size = 1) => {
      targetContext.globalAlpha = alpha
      targetContext.fillRect(
        Math.round(x - size * DOT_RADIUS),
        Math.round(y - size * DOT_RADIUS),
        size,
        size
      )
    }

    const drawStaticDots = (targetContext, left, top, right, bottom) => {
      const startX = Math.floor(left / DOT_SPACING) * DOT_SPACING
      const startY = Math.floor(top / DOT_SPACING) * DOT_SPACING
      const endX = Math.ceil(right / DOT_SPACING) * DOT_SPACING
      const endY = Math.ceil(bottom / DOT_SPACING) * DOT_SPACING

      targetContext.fillStyle = "#d9d9d9"

      for (let y = startY; y <= endY; y += DOT_SPACING) {
        for (let x = startX; x <= endX; x += DOT_SPACING) {
          drawDot(targetContext, x, y, (x + y) % 20 === 0 ? 0.14 : 0.075)
        }
      }

      targetContext.globalAlpha = 1
    }

    const paintStaticBuffer = () => {
      bufferContext.clearRect(0, 0, width, height)
      bufferContext.fillStyle = "#050505"
      bufferContext.fillRect(0, 0, width, height)
      drawStaticDots(
        bufferContext,
        -DOT_SPACING,
        -DOT_SPACING,
        width + DOT_SPACING,
        height + DOT_SPACING
      )
    }

    const paintCachedBackground = () => {
      context.clearRect(0, 0, width, height)
      context.globalAlpha = 1
      context.drawImage(bufferCanvas, 0, 0, width, height)
    }

    const requestDraw = () => {
      if (frameId !== null) return
      frameId = window.requestAnimationFrame(draw)
    }

    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2)
      width = window.innerWidth
      height = window.innerHeight
      canvas.width = Math.max(1, Math.round(width * dpr))
      canvas.height = Math.max(1, Math.round(height * dpr))
      bufferCanvas.width = canvas.width
      bufferCanvas.height = canvas.height
      canvas.style.width = `${width}px`
      canvas.style.height = `${height}px`
      context.setTransform(dpr, 0, 0, dpr, 0, 0)
      bufferContext.setTransform(dpr, 0, 0, dpr, 0, 0)
      paintStaticBuffer()
      paintCachedBackground()
    }

    const draw = () => {
      frameId = null
      const now = window.performance.now()
      paintCachedBackground()

      const idleTime = now - lastPointerTime
      const returnProgress = Math.min(
        1,
        Math.max(0, (idleTime - IDLE_HOLD_MS) / RETURN_DURATION_MS)
      )
      const influenceStrength = (1 - returnProgress) ** 2

      if (targetPointer) {
        pointer = pointer
          ? {
              x: pointer.x + (targetPointer.x - pointer.x) * POINTER_FOLLOW_EASE,
              y: pointer.y + (targetPointer.y - pointer.y) * POINTER_FOLLOW_EASE,
            }
          : { ...targetPointer }
      } else if (pointer && influenceStrength <= 0.01) {
        pointer = null
      }

      const influencePointer = pointer && influenceStrength > 0.01 ? pointer : null
      const radiusSquared = REPEL_RADIUS * REPEL_RADIUS

      if (influencePointer) {
        const repaintRadius = REPEL_RADIUS + REPEL_STRENGTH + DOT_SPACING * 2
        const repaintLeft = Math.max(-DOT_SPACING, influencePointer.x - repaintRadius)
        const repaintTop = Math.max(-DOT_SPACING, influencePointer.y - repaintRadius)
        const repaintRight = Math.min(width + DOT_SPACING, influencePointer.x + repaintRadius)
        const repaintBottom = Math.min(height + DOT_SPACING, influencePointer.y + repaintRadius)

        context.save()
        context.beginPath()
        context.rect(repaintLeft, repaintTop, repaintRight - repaintLeft, repaintBottom - repaintTop)
        context.clip()
        context.fillStyle = "#050505"
        context.fillRect(repaintLeft, repaintTop, repaintRight - repaintLeft, repaintBottom - repaintTop)
        context.fillStyle = "#d9d9d9"

        const startX = Math.floor(repaintLeft / DOT_SPACING) * DOT_SPACING
        const startY = Math.floor(repaintTop / DOT_SPACING) * DOT_SPACING
        const endX = Math.ceil(repaintRight / DOT_SPACING) * DOT_SPACING
        const endY = Math.ceil(repaintBottom / DOT_SPACING) * DOT_SPACING

        for (let y = startY; y <= endY; y += DOT_SPACING) {
          for (let x = startX; x <= endX; x += DOT_SPACING) {
            let drawX = x
            let drawY = y
            let alpha = (x + y) % 20 === 0 ? 0.14 : 0.075
            let dotSize = 1

            const dx = x - influencePointer.x
            const dy = y - influencePointer.y
            const distanceSquared = dx * dx + dy * dy

            if (distanceSquared < radiusSquared && distanceSquared > 0.01) {
              const distance = Math.sqrt(distanceSquared)
              const force = (1 - distance / REPEL_RADIUS) ** 2
              const activeForce = force * influenceStrength
              const displacement = activeForce * REPEL_STRENGTH
              drawX += (dx / distance) * displacement
              drawY += (dy / distance) * displacement
              alpha += activeForce * 0.22
              dotSize += activeForce * 1.35
            }

            drawDot(context, drawX, drawY, alpha, Math.max(1, Math.round(dotSize)))
          }
        }

        context.restore()
      }

      context.globalAlpha = 1

      if (influenceStrength > 0.01) {
        frameId = window.requestAnimationFrame(draw)
      }
    }

    const handlePointerMove = event => {
      targetPointer = { x: event.clientX, y: event.clientY }
      lastPointerTime = window.performance.now()
      requestDraw()
    }

    const handlePointerLeave = () => {
      targetPointer = null
      lastPointerTime = window.performance.now()
      requestDraw()
    }

    resize()
    requestDraw()

    window.addEventListener("resize", resize)
    window.addEventListener("pointermove", handlePointerMove, { passive: true })
    window.addEventListener("pointerleave", handlePointerLeave)
    window.addEventListener("blur", handlePointerLeave)

    return () => {
      document.documentElement.classList.remove("has-interactive-dot-background")
      if (frameId !== null) window.cancelAnimationFrame(frameId)
      window.removeEventListener("resize", resize)
      window.removeEventListener("pointermove", handlePointerMove)
      window.removeEventListener("pointerleave", handlePointerLeave)
      window.removeEventListener("blur", handlePointerLeave)
    }
  }, [reduceMotion])

  if (reduceMotion) return null

  return (
    <canvas
      ref={canvasRef}
      className="interactive-dot-background"
      aria-hidden="true"
    />
  )
}

export default InteractiveDotBackground
